// src/App.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './App.css';
import DocumentList from './components/DocumentList';
import DocumentViewer from './components/DocumentViewer';
import DocumentHistory from './components/DocumentHistory';
import FanView from './components/FanView';
import NavigationBreadcrumb from './components/NavigationBreadcrumb';
import StatusBar from './components/StatusBar';
import SearchModal from './components/SearchModal';
import ComparisonSelector from './components/ComparisonSelector';
import EditorToolbar from './components/EditorToolbar';
import './components/EditorToolbar.css';
import { useBranding } from './contexts/BrandingContext';
import { 
  FileText, 
  FolderOpen, 
  GitBranch, 
  Clock, 
  Edit, 
  Eye,
  Save,
  GitCommit,
  History,
  Grid3x3,
  Search,
  GitCompare,
  Upload,
  Briefcase
} from 'lucide-react';

// Define a type for Project
export interface Project {
  id: string;
  name: string;
}

export interface Document {
  name: string;
  path: string;
  size: number;
  modified: Date;
  gitStatus?: string | null;
  content?: string;
  created?: Date;
  commits?: number;
  authors?: string[];
  links?: string[];
  tags?: string[];
}

export interface GitCommit {
  hash: string;
  date: string;
  message: string;
  author: string;
  email: string;
}

export interface NavigationState {
  document: Document;
  section?: string;
  content: string;
}

// Define the API base URL based on the browser's current URL at runtime
const getApiBaseUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8080/api';
  }
  // This will be used for any non-localhost environment, including all Cloudflare Pages deployments
  return 'https://api-tf.amodel.org/api';
};

const API_BASE_URL = getApiBaseUrl();

function App() {
  // Get branding configuration
  const { branding, isDarkMode, toggleDarkMode } = useBranding();
  
  // State management
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [documentContent, setDocumentContent] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [leftPanelMode, setLeftPanelMode] = useState<'tree' | 'history'>('tree');
  const [documentHistory, setDocumentHistory] = useState<GitCommit[]>([]);
  const [selectedCommit, setSelectedCommit] = useState<GitCommit | null>(null);
  const [historicalContent, setHistoricalContent] = useState<string | null>(null);
  const [navigationHistory, setNavigationHistory] = useState<NavigationState[]>([]);
  const [showFanView, setShowFanView] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCommitDialog, setShowCommitDialog] = useState(false);
  const [commitMessage, setCommitMessage] = useState('');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [compareDocument, setCompareDocument] = useState<Document | null>(null);
  const [compareContent, setCompareContent] = useState<string | null>(null);
  const [showComparisonSelector, setShowComparisonSelector] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(320);
  const [isResizing, setIsResizing] = useState(false);
  const [isDocumentReadOnly, setIsDocumentReadOnly] = useState(false);
  const [documentMetadata, setDocumentMetadata] = useState<any>(null);
  const [authToken, setAuthToken] = useState<string | null>(null); // Store auth token

  // MOCK AUTH: In a real app, you would get this from a login process.
  useEffect(() => {
    // This is a temporary token for testing. Replace with a real login flow.
    const testToken = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOiJjMGY0NjBiZC00Y2EwLTQzZWUtODk0Yi02MTdjYzU4NTc5OTEiLCJlbWFpbCI6InRlc3RAZXhhbXBsZS5jb20iLCJpYXQiOjE3NTc1MzA1NjYsImV4cCI6MTc1ODEzNTM2Nn0.EkFEEeCBMs11Br6Hc99tzrOqB8JY7Xau1U9BG-YhKjE";
    setAuthToken(testToken);
  }, []);

  // Fetch projects on component mount
  useEffect(() => {
    if (!authToken) return;
    loadProjects();
  }, [authToken]);

  // Set selected project when projects load
  useEffect(() => {
    if (projects.length > 0 && !selectedProject) {
      setSelectedProject(projects[0]);
    }
  }, [projects, selectedProject]);

  // Fetch documents when a project is selected
  useEffect(() => {
    if (selectedProject) {
      loadDocuments(selectedProject.id);
    } else {
      setDocuments([]); // Clear documents if no project is selected
    }
  }, [selectedProject]);

  // Fetch projects from the backend
  const loadProjects = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/projects`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch projects: ${response.statusText}`);
      }
      const projectData = await response.json();
      setProjects(projectData);
      // Automatically select the first project
      if (projectData.length > 0) {
        setSelectedProject(projectData[0]);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      setError(error instanceof Error ? error.message : 'Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  // Load documents for a given project
  const loadDocuments = async (projectId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/documents?projectId=${projectId}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch documents: ${response.statusText}`);
      }
      const docs = await response.json();
      setDocuments(docs);
    } catch (error) {
      console.error('Error loading documents:', error);
      setError(error instanceof Error ? error.message : 'Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  // Select and load a document's content
  const selectAndLoadDocument = async (doc: Document) => {
    // This function would now fetch the full document content from the API
    // For now, we assume the content is already loaded or we can add another API call
    setSelectedDocument(doc);
    setDocumentContent(doc.content || ''); // Use content if available
    setHasUnsavedChanges(false);
    setIsEditMode(false);
  };

  // Create a new document
  const createDocument = async (name: string, folder?: string) => {
    if (!selectedProject) {
      setError("No project selected to add a document to.");
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const path = folder ? `${folder}/${name}` : name;
      const initialContent = `# ${name.replace('.md', '')}\n\nCreated: ${new Date().toISOString()}`;
      
      const response = await fetch(`${API_BASE_URL}/documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          projectId: selectedProject.id,
          name,
          path,
          content: initialContent
        })
      });

      if (!response.ok) {
        let errorMessage = `Failed to create document: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.error || errorData.message) {
            errorMessage = errorData.error || errorData.message;
          }
        } catch (e) {
          // If response is not JSON, use status text
          if (response.statusText) {
            errorMessage += ` ${response.statusText}`;
          }
        }
        throw new Error(errorMessage);
      }

      // Refresh the document list to show the new document
      await loadDocuments(selectedProject.id);
    } catch (error) {
      console.error('Error creating document:', error);
      setError(error instanceof Error ? error.message : 'Failed to create document');
    } finally {
      setIsLoading(false);
    }
  };

  // Create a new folder (by creating a placeholder document)
  const createFolder = async (name: string) => {
    // In this model, a "folder" is just part of the document's path.
    // We can create a placeholder file to represent the folder.
    await createDocument('.placeholder', name);
  };

  // Handler for when a document is selected from the search modal
  const handleSearchSelect = (path: string) => {
    const doc = documents.find(d => d.path === path);
    if (doc) {
      selectAndLoadDocument(doc);
    }
  };

  // Panel resize handlers
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = e.clientX;
      const constrainedWidth = Math.min(Math.max(newWidth, 250), 500);
      setLeftPanelWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  return (
    <div className="app">
      {/* Header */}
      <header className="app-header">
        <div className="header-left">
          <FileText className="app-icon" />
          <h1>Time Files</h1>
          {selectedProject && (
            <span className="current-directory">{selectedProject.name}</span>
          )}
        </div>
        
        <div className="header-right">
          <button 
            onClick={() => setShowSearchModal(true)}
            className="btn-search"
            title="Search (⌘K)"
          >
            <Search size={18} className="search-icon" />
            <span className="search-text">Search Engine</span>
            <span className="search-shortcut">⌘K</span>
          </button>

          <button 
            onClick={() => setShowComparisonSelector(true)}
            className="btn btn-primary"
            title="Compare Files (⌘D)"
          >
            <GitCompare size={18} />
            <span>Compare</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="app-content">
        {/* Left Panel */}
        <aside className="left-panel" style={{ width: `${leftPanelWidth}px` }}>
          <div className="panel-header">
            {/* Project Selector Dropdown */}
            <div className="project-selector">
              <Briefcase size={16} />
              <select
                value={selectedProject?.id || ''}
                onChange={(e) => {
                  const projectId = e.target.value;
                  const project = projects.find(p => p.id === projectId);
                  setSelectedProject(project || null);
                }}
                disabled={isLoading}
              >
                <option value="" disabled>Select a Project</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            {selectedProject && (
              <button className="btn-import" title="Import PDF, TXT, or DOCX file">
                <Upload size={16} />
                Import File
              </button>
            )}
          </div>
          <div className="panel-tabs">
            <button
              className={`tab ${leftPanelMode === 'tree' ? 'active' : ''}`}
              onClick={() => setLeftPanelMode('tree')}
            >
              <FileText size={16} />
              Documents
            </button>
            
            {selectedDocument && (
              <button
                className={`tab ${leftPanelMode === 'history' ? 'active' : ''}`}
                onClick={() => setLeftPanelMode('history')}
              >
                <Clock size={16} />
                History
              </button>
            )}
          </div>
          
          <div className="panel-content">
            {leftPanelMode === 'tree' ? (
              <DocumentList
                documents={documents}
                selectedDocument={selectedDocument}
                onSelectDocument={selectAndLoadDocument}
                onRefresh={() => selectedProject && loadDocuments(selectedProject.id)}
                onCreateDocument={createDocument}
                onCreateFolder={createFolder}
              />
            ) : (
              <DocumentHistory
                history={documentHistory}
                selectedCommit={selectedCommit}
                onSelectCommit={() => {}} // Placeholder
                onReturnToCurrent={() => {}} // Placeholder
              />
            )}
          </div>
          
          {/* Resize Handle */}
          <div 
            className="resize-handle"
            onMouseDown={(e) => {
              e.preventDefault();
              setIsResizing(true);
            }}
          />
        </aside>

        {/* Document Viewer/Editor */}
        <main className="document-area">
          {selectedDocument ? (
            <>
              <DocumentViewer
                document={selectedDocument}
                content={documentContent}
                isEditMode={isEditMode}
                onContentChange={setDocumentContent}
                // Pass other necessary props, placeholders for now
                isHistorical={false}
                selectedCommit={null}
                compareDocument={null}
                compareContent={null}
                onDocumentLink={() => {}}
                isReadOnly={false}
                onClone={() => {}}
                onProtect={() => {}}
              />
            </>
          ) : (
            <div className="empty-state">
              <FileText size={64} />
              <h2>{selectedProject ? 'Select a document' : 'Select a project'}</h2>
              <p>{selectedProject ? 'Select a document from the left panel to view or edit' : 'Choose a project from the dropdown to see its documents'}</p>
            </div>
          )}
        </main>
      </div>

      {/* Status Bar */}
      <StatusBar
        isGitRepo={false} // Git features disabled for web version for now
        gitStatus={null}
        hasUnsavedChanges={hasUnsavedChanges}
        isLoading={isLoading}
        error={error}
        onClearError={() => setError(null)}
      />

      {/* Modals */}
      <SearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        documents={documents}
        currentPath={selectedDocument?.path}
        onSelectDocument={handleSearchSelect}
        gitHistory={new Map()}
      />
      {showComparisonSelector && (
        <ComparisonSelector
          onClose={() => setShowComparisonSelector(false)}
          currentDocument={selectedDocument ? {...selectedDocument, content: selectedDocument.content || ''} : null}
          recentFiles={documents.map(d => ({...d, content: d.content || ''}))}
          isGitRepo={false}
        />
      )}
    </div>
  );
}

export default App;